// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/hooks/useBrand';
import { type BrandConfig } from '@/api/brandService';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';

// Login sol paneli iki moda ayrılır:
// 1) Markalı app (eportal, yikamatik, smarterp…): SADECE o app'in kendi kimliği —
//    logo, ad, slogan, kendi özellikleri (brand.loginFeatures), kendi renkleri.
//    Başka ürün adı GÖSTERİLMEZ (4-katman ürün izolasyonu). Custom app kendine has görünür.
// 2) Çıplak platform (subdomain yok / elpocore): ELPO ekosistem vitrini (ürün carousel'i).

// --- Çıplak platform vitrini (ekosistem) ---
type Slide = { kind: 'product'; key: string; logoUrl: string; gradient: string };

const PRODUCTS: Slide[] = [
  { kind: 'product', key: 'cortex', logoUrl: '/products/cortex.svg', gradient: 'linear-gradient(135deg,#0d9488 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'smarterp', logoUrl: '/products/smarterp.svg', gradient: 'linear-gradient(135deg,#0ea5e9 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'elpocore', logoUrl: '/products/elpocore.svg', gradient: 'linear-gradient(135deg,#0091B3 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'asistanim', logoUrl: '/products/asistanim.svg', gradient: 'linear-gradient(135deg,#10b981 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'yikamatik', logoUrl: '/brand/yikamatik/logo.svg', gradient: 'linear-gradient(135deg,#06b6d4 0%,#0c1b3d 100%)' },
  // SmartSarj'in GERCEK yuvarlak logosu PNG'dedir; yanindaki .svg uydurma bir yer tutucuydu
  // (yesil kare + simsek) — kullanici 14.09.2026: "gercek logosunu kullan".
  { kind: 'product', key: 'smartsarj', logoUrl: '/products/smartsarj.png', gradient: 'linear-gradient(135deg,#14b8a6 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'entegrator', logoUrl: '/products/entegrator.png', gradient: 'linear-gradient(135deg,#f59e0b 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'arizaYonetim', logoUrl: '/products/ariza-yonetim.png', gradient: 'linear-gradient(135deg,#ff6a13 0%,#0c1b3d 100%)' },
  { kind: 'product', key: 'kapiyaGel', logoUrl: '/products/kapiya-gel.svg', gradient: 'linear-gradient(135deg,#38bdf8 0%,#0c1b3d 100%)' },
];

const SLIDES: Slide[] = PRODUCTS;


// Musteri sayisi KALDIRILDI (kullanici karari 08.09.2026): "300+" olculmus bir rakam
// degildi. Dogrulanmamis sayi giris ekraninda durursa ilk soruda savunulamaz.
const STATS = [
  { value: 'AI',    label: 'Destekli' },
  { value: '7/24',  label: 'Otomasyon' },
  { value: '%99.9', label: 'Uptime' },
];

function HeroSlider(): ReactElement {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = window.setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 4500);
    return () => window.clearInterval(t);
  }, [paused]);

  const cur = SLIDES[idx]!;
  const prev = () => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setIdx((i) => (i + 1) % SLIDES.length);

  return (
    <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-card/5">
        <div className="px-12 py-7 relative flex items-center min-h-[168px]" style={{ background: cur.gradient }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%,rgba(255,255,255,0.4),transparent 50%)' }} />
          <div className="relative z-10 flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-white ring-1 ring-black/5 flex items-center justify-center shrink-0 overflow-hidden shadow-md">
              {/* Giriş ekranı ilk açılışta ağır geliyordu: bu kutu 56x56 çizerken görseller 1000x1000
                  PNG'ydi (entegrator 183 KB, arıza-yönetim 158 KB, smartşarj 153 KB — toplam 494 KB).
                  Görseller 128x128'e indirildi; burada da tarayıcıya boyut ve öncelik bildiriliyor ki
                  yerleşim kaymasın ve slayt görseli metnin önüne geçmesin (ölçüm 2026-08-31). */}
              <img
                src={`${import.meta.env.BASE_URL}${cur.logoUrl.replace(/^\//, '')}`}
                alt={t(`login.urunler.${cur.key}.ad`)}
                width={56}
                height={56}
                decoding="async"
                fetchPriority="low"
                className="w-full h-full object-contain p-1.5"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <h3 className="text-white text-lg font-bold leading-none">{t(`login.urunler.${cur.key}.ad`)}</h3>
                <span className="text-white/70 text-[12px] font-bold tracking-wider uppercase">{t(`login.urunler.${cur.key}.etiket`)}</span>
              </div>
              <p className="text-white/85 text-xs leading-relaxed">{t(`login.urunler.${cur.key}.aciklama`)}</p>
            </div>
          </div>
        </div>
        <button onClick={prev} aria-label="prev" className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black hover:bg-black flex items-center justify-center transition-all">
          <ChevronLeft className="size-4 text-white" />
        </button>
        <button onClick={next} aria-label="next" className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black hover:bg-black flex items-center justify-center transition-all">
          <ChevronRight className="size-4 text-white" />
        </button>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-3">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-teal-300' : 'w-1.5 bg-card hover:bg-card'}`} />
        ))}
      </div>
    </div>
  );
}

function PlatformShowcasePanel({ brand, showProducts = true }: { brand?: BrandConfig; showProducts?: boolean }): ReactElement {
  const { t } = useTranslation();
  const ozellikler = t('login.sideFeatures', { returnObjects: true }) as string[];
  const appName = brand?.displayName ?? 'SmartERP';
  // ELPO resmi marka paleti (marka kılavuzu, docs/BRAND_COLORS.md): LACİVERT #003B5C zemin +
  // TURKUAZ #0091B3 + TURUNCU #FF6A13 aksan. Mor/krem YOK; düşük-kırmızı lacivert/turkuaz kötü
  // kalibre monitörde bile mora kaymaz.
  return (
    <div className="hidden lg:flex flex-col w-[460px] shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(175deg,#0a1428 0%,#0c1b3d 40%,#0a1730 70%,#081225 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1.5px 1.5px,rgba(255,255,255,0.04) 1.5px,transparent 0)', backgroundSize: '28px 28px' }} />
      <div className="absolute top-0 inset-x-0 h-80 pointer-events-none elpo-glow-pulse" style={{ background: 'radial-gradient(ellipse at 50% -10%,rgba(0,145,179,0.30) 0%,transparent 70%)' }} />
      <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full pointer-events-none elpo-float elpo-glow-pulse" style={{ background: 'radial-gradient(circle,#0091B3,transparent)' }} />
      <div className="absolute top-1/3 -left-16 w-64 h-64 rounded-full pointer-events-none elpo-float" style={{ background: 'radial-gradient(circle,rgba(255,106,19,0.16),transparent)', animationDelay: '-3s' }} />

      <div className="relative z-10 flex flex-col h-full px-10 py-10">
        <div className="mb-8 elpo-reveal" style={{ ['--d' as string]: '40ms' }}>
          <img src={`${import.meta.env.BASE_URL}elpo-logo-full.png`} alt="ELPO" className="h-9 object-contain" style={{ filter: 'brightness(0) invert(1)', opacity: 0.92 }} />
          <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, letterSpacing: '-0.5px', fontSize: '28px', color: '#ffffff', marginTop: '8px' }}>{appName}</div>
        </div>

        <div className="mb-6 elpo-reveal" style={{ ['--d' as string]: '130ms' }}>
          <h2 className="text-white text-[22px] font-bold leading-snug mb-3">
            {t('login.sideHeadline')}<br />
            <span className="elpo-shine-text" style={{ background: 'linear-gradient(90deg,#5fd2e8,#0091B3,#5fd2e8)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t('login.sideHeadlineAccent')}</span>
          </h2>
          <p className="text-white/85 text-sm leading-relaxed">
            {t('login.sideIntro')}
          </p>
        </div>

        {showProducts && (
          <div className="elpo-reveal" style={{ ['--d' as string]: '220ms' }}>
            <HeroSlider />
          </div>
        )}

        <div className="space-y-2.5 flex-1 mt-6 elpo-reveal" style={{ ['--d' as string]: '300ms' }}>
          {ozellikler.map((metin, i) => (
            <div key={i} className="flex items-center gap-3 group">
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ background: 'rgba(0,145,179,0.22)', border: '1px solid rgba(0,145,179,0.42)' }}>
                <CheckCircle2 className="size-3 text-cyan-300" />
              </div>
              <span className="text-white/90 text-sm transition-colors group-hover:text-white">{metin}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-6 mt-8 elpo-reveal" style={{ ['--d' as string]: '380ms' }}>
          {/* Sutun sayisi listeden turetilir: sabit grid-cols-4 idi, bir madde
              cikarilinca sagda bos sutun kaliyordu. */}
          <div
            className="grid gap-3 mb-5"
            style={{ gridTemplateColumns: `repeat(${STATS.length}, minmax(0, 1fr))` }}
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="text-white font-bold text-lg leading-none">{s.value}</p>
                <p className="text-teal-300/50 text-[12px] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-teal-300/35 text-[12px]">ELPO Ekosistemi — © 2026</p>
        </div>
      </div>
    </div>
  );
}

// --- Markalı app paneli (app'e özgü, başka ürün GÖSTERMEZ) ---

// Hex rengi koyulaştır/açar — percent negatif = koyu. Marka rengi gradient'e dönüşür.
function shade(hex: string, percent: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  const clamp = (c: number) => Math.max(0, Math.min(255, Math.round(c + (percent / 100) * 255)));
  const r = clamp((n >> 16) & 0xff);
  const g = clamp((n >> 8) & 0xff);
  const b = clamp(n & 0xff);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Branded izolasyon paneli — şu an login tüm app'lerde ekosistem vitrini gösteriyor (kullanıcı kararı);
// bu panel ileride app-bazlı izolasyon istenirse yeniden devreye alınabilir diye korunuyor.
export function BrandedAppPanel({ brand }: { brand: BrandConfig }): ReactElement {
  const primary = brand.primaryColor || '#0d9488';
  // Login panelinde gözü tırmalayan sıcak (turuncu) aksan yerine primary'den türeyen SERİN turkuaz aksan
  // (glow/rozet/başlık/checkmark). Marka logosu değişmez; sadece dekoratif tonlar serinler.
  const accent = shade(primary, 38);
  const c1 = shade(primary, -56);
  const c2 = shade(primary, -34);
  const c3 = shade(primary, -12);
  const heading = brand.loginGreeting ?? brand.displayName;
  const subtitle = brand.loginSubtitle ?? brand.tagline ?? '';
  const features = brand.loginFeatures.length > 0 ? brand.loginFeatures : (brand.tagline ? [brand.tagline] : []);

  return (
    <div className="hidden lg:flex flex-col w-[460px] shrink-0 relative overflow-hidden"
      style={{ background: `linear-gradient(165deg, ${c1} 0%, ${c2} 55%, ${c3} 100%)` }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1.5px 1.5px,rgba(255,255,255,0.05) 1.5px,transparent 0)', backgroundSize: '28px 28px' }} />
      <div className="absolute top-0 inset-x-0 h-80 pointer-events-none elpo-glow-pulse" style={{ background: `radial-gradient(ellipse at 50% -10%, ${accent}33 0%, transparent 70%)` }} />
      <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full pointer-events-none elpo-float elpo-glow-pulse" style={{ background: `radial-gradient(circle, ${accent}, transparent)` }} />
      <div className="absolute top-1/3 -left-16 w-64 h-64 rounded-full pointer-events-none elpo-float" style={{ background: `radial-gradient(circle, ${primary}40, transparent)`, animationDelay: '-3s' }} />

      <div className="relative z-10 flex flex-col h-full px-10 py-12">
        {/* Ürün logosu adı zaten taşır — yanında yalnız sade "ELPO" yazısı (ikinci logo GİBİ DURMASIN;
            kullanıcı kararı 2026-08-15: "iki logo olmasın, ELPO'yu yazıyla yaz, logo gibi de koyma").
            Büyük ad başlığı da yazılmaz ("logoda yazıyor zaten"). Logosu olmayan marka eski görünümde. */}
        <div className="mb-6 flex items-center gap-3 elpo-reveal" style={{ ['--d' as string]: '40ms' }}>
          {brand.logo ? (
            <>
              <img src={brand.logo} alt={brand.displayName} className="h-14 object-contain" />
              {/* elpo-logo-full.png'den kırpılmış YALNIZ "ELPO" yazısı (yuvarlak ikon yok) — kullanıcı
                  kararı 2026-08-15: metin fontu logodaki kalınlığı tutmadı, gerçek wordmark kullanılır. */}
              <img src={`${import.meta.env.BASE_URL}brand/cxcentral/elpo-wordmark.png`} alt="ELPO" className="h-7 object-contain" style={{ filter: 'brightness(0) invert(1)', opacity: 0.95 }} />
            </>
          ) : (
            <img src={`${import.meta.env.BASE_URL}elpo-logo-full.png`} alt="ELPO" className="h-8 object-contain" style={{ filter: 'brightness(0) invert(1)', opacity: 0.92 }} />
          )}
        </div>

        {brand.panelTag && (
          <div className="mb-8 inline-flex w-fit items-center rounded-full px-3 py-1 text-[12px] font-bold uppercase tracking-[0.18em] text-white elpo-reveal"
            style={{ background: `${accent}26`, border: `1px solid ${accent}66`, color: accent, ['--d' as string]: '110ms' }}>
            {brand.panelTag}
          </div>
        )}

        <div className="mb-8 elpo-reveal" style={{ ['--d' as string]: '170ms' }}>
          {!brand.logo && (
            <div className="elpo-shine-text" style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, letterSpacing: '-0.5px', fontSize: '34px', lineHeight: 1.2, paddingBottom: '0.12em', background: `linear-gradient(90deg,#ffffff,${accent},#ffffff)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {brand.displayName}
            </div>
          )}
          {brand.tagline && (
            <div className="mt-2 text-sm font-medium" style={{ color: `${accent}` }}>{brand.tagline}</div>
          )}
        </div>

        <div className="mb-8 elpo-reveal" style={{ ['--d' as string]: '240ms' }}>
          <h2 className="text-white text-[22px] font-bold leading-snug mb-3">{heading}</h2>
          {subtitle && <p className="text-white/65 text-sm leading-relaxed">{subtitle}</p>}
        </div>

        <div className="space-y-3 flex-1 elpo-reveal" style={{ ['--d' as string]: '320ms' }}>
          {features.map((text, i) => (
            <div key={i} className="flex items-center gap-3 group">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ background: `${accent}26`, border: `1px solid ${accent}4d` }}>
                <CheckCircle2 className="size-3.5" style={{ color: accent }} />
              </div>
              <span className="text-white/80 text-sm transition-colors group-hover:text-white">{text}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-6 mt-8 elpo-reveal" style={{ ['--d' as string]: '400ms' }}>
          <p className="text-white/35 text-[12px]">{brand.footerText ?? `© ${brand.displayName}`}</p>
        </div>
      </div>
    </div>
  );
}

export function EcosystemSidePanel(): ReactElement {
  const brand = useBrand();
  // Varsayılan: HER app login'inde Elpo ürün ekosistemi showcase'i (kullanıcı kararı 2026-06-23).
  // İstisna brand VERİSİYLE seçilir (ürün adı hardcode edilmez): brand_json'da loginPanel="branded"
  // olan ürün, yan panelde YALNIZ kendi kimliğini gösterir (kullanıcı kararı 2026-08-15, ELPO CX).
  if (brand.loginPanel === 'branded') {
    return <BrandedAppPanel brand={brand} />;
  }
  return <PlatformShowcasePanel brand={brand} showProducts={true} />;
}
